import React, { createContext, useContext, useEffect, useState } from 'react';
import { CartServicesReactReadyHelper } from '../drawCartServices';
import { JbCart } from '@jbhi-fi/apis/src/shopify/types/JbCart';

const CART_UPDATE_READY = 'cartUpdateReady';

export enum CartLocation {
  cart = 'cart',
  checkout = 'checkout',
  minicart = 'minicart',
}

export interface CartContextInterface {
  cart: JbCart;
  location: CartLocation;
}

export const CartContext = createContext<CartContextInterface | undefined>(undefined);

const getLocation = (): CartLocation => {
  if (window.location.pathname === '/cart') return CartLocation.cart;
  if (window.Shopify?.Checkout) return CartLocation.checkout;
  return CartLocation.minicart;
};

export const CartContextProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [cart, setCart] = useState<JbCart | undefined>(undefined);
  const location = getLocation();

  const handleCartUpdateReady = (event: CustomEvent) => {
    if (event.detail.cart) setCart(event.detail.cart);
  };

  useEffect(() => {
    window.addEventListener(CART_UPDATE_READY, handleCartUpdateReady);
    CartServicesReactReadyHelper.setIsReady(true);
    return () => {
      window.removeEventListener(CART_UPDATE_READY, handleCartUpdateReady);
    };
  }, []);

  return <CartContext.Provider value={{ cart, location }}>{children}</CartContext.Provider>;
};

export class CartContextHelper {
  static broadcastCartUpdate = (cart: JbCart) => {
    if (!CartServicesReactReadyHelper.getIsReady()) {
      setTimeout(() => CartContextHelper.broadcastCartUpdate(cart), 50);
    } else {
      window.dispatchEvent(new CustomEvent(CART_UPDATE_READY, { detail: { cart } }));
    }
  };
}

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (context === undefined || context === null) {
    throw new Error('useCartContext must be used within a useCartContextProvider');
  }
  return context;
};
